import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getTimbreDesignPageApi, mapTimbreDesignRowToVoiceToneItem } from "../../service/timbreDesign";
import { getPublicVoiceTonePageApi } from "../../service/voicePublic";
import {
  isPublicVoiceAvailable,
  normalizeVoiceCloneFlags,
} from "../voicePublic/publicVoiceMultiFetch";
import { toMineVoiceOption, type VoiceOption } from "../voiceAi/voiceSelectShared";

const PAGE_SIZE = 30;

const toPublicVoiceOption = (item: VoicePublic.VoiceToneItem): VoiceOption => ({
  ...toMineVoiceOption(item),
  source: "public",
});

type ListState = {
  pageNo: number;
  keyword: string;
  loading: boolean;
  done: boolean;
};

const createState = (): ListState => ({ pageNo: 0, keyword: "", loading: false, done: false });

/**
 * 口播音色：音色库「公共音色」+「我的音色」，不用 AI 音频 / 声音演绎那套。
 * 两个列表都按页增量加载（瀑布流），滚到底再拉下一批。
 */
export function useDigitalHumanVoiceLists() {
  const [publicVoices, setPublicVoices] = useState<VoiceOption[]>([]);
  const [mineVoices, setMineVoices] = useState<VoiceOption[]>([]);
  const [publicLoading, setPublicLoading] = useState(false);
  const [mineLoading, setMineLoading] = useState(false);
  const [hasMorePublic, setHasMorePublic] = useState(false);
  const [hasMoreMine, setHasMoreMine] = useState(false);

  const publicStateRef = useRef<ListState>(createState());
  const mineStateRef = useRef<ListState>(createState());
  const publicReqRef = useRef(0);
  const mineReqRef = useRef(0);

  const loadPublic = useCallback(async (reset: boolean) => {
    const state = publicStateRef.current;
    if (state.loading) return;
    if (!reset && state.done) return;

    state.loading = true;
    setPublicLoading(true);
    const reqId = ++publicReqRef.current;
    const pageNo = reset ? 1 : state.pageNo + 1;
    const keyword = state.keyword;

    try {
      const res = await getPublicVoiceTonePageApi({
        pageNo,
        pageSize: PAGE_SIZE,
        published: true,
        ...(keyword ? { voiceName: keyword } : {}),
      });
      if (reqId !== publicReqRef.current) return;

      const raw =
        res.code === 0 && Array.isArray(res.data?.list)
          ? res.data.list.map(normalizeVoiceCloneFlags)
          : [];
      const items = raw.filter(isPublicVoiceAvailable).map(toPublicVoiceOption);

      state.pageNo = pageNo;
      state.done = raw.length < PAGE_SIZE;
      setHasMorePublic(!state.done);
      setPublicVoices((prev) => {
        if (reset) return items;
        const seen = new Set(prev.map((v) => v.id));
        return [...prev, ...items.filter((v) => !seen.has(v.id))];
      });
    } catch {
      /* ignore */
    } finally {
      if (reqId === publicReqRef.current) {
        state.loading = false;
        setPublicLoading(false);
      }
    }
  }, []);

  const loadMine = useCallback(async (reset: boolean) => {
    const state = mineStateRef.current;
    if (state.loading) return;
    if (!reset && state.done) return;

    state.loading = true;
    setMineLoading(true);
    const reqId = ++mineReqRef.current;
    const pageNo = reset ? 1 : state.pageNo + 1;
    const keyword = state.keyword;

    try {
      const res = await getTimbreDesignPageApi({
        pageNo,
        pageSize: PAGE_SIZE,
        ...(keyword ? { voiceName: keyword } : {}),
      });
      if (reqId !== mineReqRef.current) return;

      const raw =
        res.code === 0 && Array.isArray(res.data?.list)
          ? res.data.list.map(mapTimbreDesignRowToVoiceToneItem)
          : [];
      const items = raw.map(toMineVoiceOption);

      state.pageNo = pageNo;
      state.done = raw.length < PAGE_SIZE;
      setHasMoreMine(!state.done);
      setMineVoices((prev) => {
        if (reset) return items;
        const seen = new Set(prev.map((v) => v.id));
        return [...prev, ...items.filter((v) => !seen.has(v.id))];
      });
    } catch {
      /* ignore */
    } finally {
      if (reqId === mineReqRef.current) {
        state.loading = false;
        setMineLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    void loadPublic(true);
    void loadMine(true);
  }, [loadPublic, loadMine]);

  /** 搜索词变化时回到第一页重新拉，避免已加载的旧结果混在里面 */
  const setPublicKeyword = useCallback(
    (keyword: string) => {
      const state = publicStateRef.current;
      if (state.keyword === keyword) return;
      state.keyword = keyword;
      state.pageNo = 0;
      state.done = false;
      state.loading = false;
      publicReqRef.current += 1;
      void loadPublic(true);
    },
    [loadPublic]
  );

  const setMineKeyword = useCallback(
    (keyword: string) => {
      const state = mineStateRef.current;
      if (state.keyword === keyword) return;
      state.keyword = keyword;
      state.pageNo = 0;
      state.done = false;
      state.loading = false;
      mineReqRef.current += 1;
      void loadMine(true);
    },
    [loadMine]
  );

  const loadMorePublic = useCallback(() => {
    void loadPublic(false);
  }, [loadPublic]);

  const loadMoreMine = useCallback(() => {
    void loadMine(false);
  }, [loadMine]);

  const allVoices = useMemo(
    () => [...publicVoices, ...mineVoices],
    [publicVoices, mineVoices]
  );

  return {
    publicVoices,
    mineVoices,
    allVoices,
    publicLoading,
    mineLoading,
    hasMorePublic,
    hasMoreMine,
    loadMorePublic,
    loadMoreMine,
    setPublicKeyword,
    setMineKeyword,
  };
}
